<template>
  <div :class="bem.b()" v-if="pageCount > 0">
    <!-- 总条数显示 -->
    <span v-if="showTotal" :class="bem.e('total')"> 共 {{ total }} 条 </span>

    <!-- 上一页按钮 -->
    <cms-button
      :class="bem.e('prev')"
      :disabled="disabled || internalCurrentPage <= 1"
      @click="prev"
    >
      <template #icon>
        <svg
          viewBox="0 0 1024 1024"
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
        >
          <path
            fill="currentColor"
            d="M609.408 149.376 277.76 489.6a32 32 0 0 0 0 44.672l331.648 340.352a29.12 29.12 0 0 0 41.728 0 30.592 30.592 0 0 0 0-42.752L339.264 511.936l311.872-319.872a30.592 30.592 0 0 0 0-42.688 29.12 29.12 0 0 0-41.728 0z"
          />
        </svg>
      </template>
    </cms-button>

    <!-- 页码列表 -->
    <ul :class="bem.e('pager')">
      <li
        v-for="pager in pagers"
        :key="pager"
        :class="[
          bem.e('number'),
          { 'is-active': pager === internalCurrentPage }
        ]"
        @click="handlePageClick(pager)"
      >
        {{ pager }}
      </li>
    </ul>

    <!-- 下一页按钮 -->
    <cms-button
      :class="bem.e('next')"
      :disabled="disabled || internalCurrentPage >= pageCount"
      @click="next"
    >
      <template #icon>
        <svg
          viewBox="0 0 1024 1024"
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
        >
          <path
            fill="currentColor"
            d="M340.864 149.312a30.592 30.592 0 0 0 0 42.752L652.736 512 340.864 831.872a30.592 30.592 0 0 0 0 42.752 29.12 29.12 0 0 0 41.728 0L714.24 534.336a32 32 0 0 0 0-44.672L382.592 149.376a29.12 29.12 0 0 0-41.728 0z"
          />
        </svg>
      </template>
    </cms-button>

    <!-- 每页条数选择器 -->
    <div v-if="showSizeChanger" :class="bem.e('sizes')">
      <cms-button>
        {{ pageSize }}条/页
        <template #icon>
          <svg
            viewBox="0 0 1024 1024"
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
          >
            <path
              fill="currentColor"
              d="M831.872 340.864 512 652.736 192.128 340.864a30.592 30.592 0 0 0-42.752 0 29.12 29.12 0 0 0 0 41.728L489.664 714.24a32 32 0 0 0 44.672 0l340.288-331.648a29.12 29.12 0 0 0 0-41.728 30.592 30.592 0 0 0-42.752 0z"
            />
          </svg>
        </template>
      </cms-button>
      <ul :class="bem.e('sizes-dropdown')">
        <li
          v-for="size in pageSizes"
          :key="size"
          :class="[bem.e('sizes-item'), { 'is-active': size === pageSize }]"
          @click="handleSizeChange(size)"
        >
          {{ size }}条/页
        </li>
      </ul>
    </div>

    <!-- 快速跳转 -->
    <div v-if="showQuickJumper" :class="bem.e('jump')">
      前往
      <cms-input
        v-model="jumpPage"
        :class="bem.e('editor')"
        @keyup.enter="handleJump"
      />
      页
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { createNamespace } from '@chenms-zj/utils/create'
import { paginationsEmits, paginationsProps } from './paginations'

defineOptions({
  name: 'cms-paginations'
})

const props = defineProps(paginationsProps)
const emit = defineEmits(paginationsEmits)
const bem = createNamespace('paginations')

// 计算总页数
const pageCount = computed(() => {
  return Math.max(1, Math.ceil(props.total / props.pageSize))
})

// 内部当前页，用于控制
const internalCurrentPage = ref(props.currentPage)

// 监听外部currentPage变化
watch(
  () => props.currentPage,
  val => {
    internalCurrentPage.value = val
  }
)

// 计算要显示的页码
const pagers = computed(() => {
  const pagerCount = props.pagerCount
  const halfPagerCount = (pagerCount - 1) / 2
  const currentPage = internalCurrentPage.value
  const pageCount = Math.ceil(props.total / props.pageSize)

  let showPrevMore = false
  let showNextMore = false

  if (pageCount > pagerCount) {
    if (currentPage > pagerCount - halfPagerCount) {
      showPrevMore = true
    }
    if (currentPage < pageCount - halfPagerCount) {
      showNextMore = true
    }
  }

  const array: number[] = []
  if (showPrevMore && !showNextMore) {
    const startPage = pageCount - (pagerCount - 2)
    for (let i = startPage; i < pageCount; i++) {
      array.push(i)
    }
  } else if (!showPrevMore && showNextMore) {
    for (let i = 2; i < pagerCount; i++) {
      array.push(i)
    }
  } else if (showPrevMore && showNextMore) {
    const offset = Math.floor(pagerCount / 2) - 1
    for (let i = currentPage - offset; i <= currentPage + offset; i++) {
      array.push(i)
    }
  } else {
    for (let i = 1; i <= pageCount; i++) {
      array.push(i)
    }
  }
  console.log(array)
  return array
})

// 页码点击处理
const handlePageClick = (page: number) => {
  if (page === internalCurrentPage.value) return
  internalCurrentPage.value = page
  emit('update:currentPage', page)
  emit('change', page, props.pageSize)
}

// 上一页
const prev = () => {
  if (props.disabled) return
  const newPage = internalCurrentPage.value - 1
  internalCurrentPage.value = newPage
  emit('update:currentPage', newPage)
  emit('change', newPage, props.pageSize)
}

// 下一页
const next = () => {
  if (props.disabled) return
  const newPage = internalCurrentPage.value + 1
  internalCurrentPage.value = newPage
  emit('update:currentPage', newPage)
  emit('change', newPage, props.pageSize)
}

// 每页条数改变
const handleSizeChange = (size: number) => {
  if (size === props.pageSize) return
  emit('update:pageSize', size)
  emit('size-change', size)
}

// 快速跳转
const jumpPage = ref('')
const handleJump = () => {
  const page = parseInt(jumpPage.value)
  if (!page || page < 1 || page > pageCount.value) {
    jumpPage.value = ''
    return
  }
  internalCurrentPage.value = page
  emit('update:currentPage', page)
  emit('change', page, props.pageSize)
  jumpPage.value = ''
}
</script>

<style scoped lang="scss">
.cms-paginations {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;

  &__total {
    color: #606266;
  }

  &__pager {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    gap: 4px;
  }

  &__number {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    min-width: 32px;
    height: 32px;
    padding: 0 4px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background: #ffffff;
    color: #606266;
    cursor: pointer;

    &:hover {
      color: #409eff;
    }

    &.is-active {
      color: #ffffff;
      background-color: #409eff;
      border-color: #409eff;
    }
  }

  &__prev,
  &__next {
    min-width: 32px;
    height: 32px;
    padding: 0;
  }

  &__sizes {
    position: relative;
    display: inline-block;

    &:hover {
      .cms-paginations__sizes-dropdown {
        display: block;
      }
    }
  }

  &__sizes-dropdown {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 0;
    padding: 4px 0;
    min-width: 100px;
    background: #ffffff;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    list-style: none;
  }

  &__sizes-item {
    padding: 8px 16px;
    cursor: pointer;

    &:hover {
      background-color: #f5f7fa;
    }

    &.is-active {
      color: #409eff;
      font-weight: bold;
    }
  }

  &__jump {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__editor {
    width: 50px;
    height: 32px;
    text-align: center;
  }
}
</style>
